// ============================================
// Creator Module - Entry Point (ES6)
// ============================================
// 모든 creator 서브모듈을 가져와서 CreatorModule로 통합

import { getAutofillTagsCache, invalidateCache } from './state.js';

// List management
import { loadCreations, renderCreationsList, openCreationDetail } from './list.js';

// Search & Sort
import { openSearch, closeSearch, handleSearch, clearSearch, openSortModal, closeSortModal, setSort } from './search.js';

// Editor
import {
  openEditor, closeEditor, switchEditorTab,
  showEditorMenu, resetEditor, deleteFromEditor,
  renderEditorImages, addEditorImage, removeEditorImage,
  selectMainImage, openEditorImagePreview, closeEditorImagePreview,
  setMainFromPreview, removeFromPreview, downloadPreviewImage,
  openCreationTypeModal, closeCreationTypeModal, selectCreationType,
  updateImageGenButtonState
} from './editor.js';

// Intro blocks
import {
  renderIntroPreview, setIntroType, updateIntroTypeButtons,
  selectIntroBlock, addIntroBlock, editIntroBlock,
  moveIntroBlock, deleteIntroBlock, addIntroImage,
  insertIntroImage, editIntroImage, replaceIntroImage,
  closeIntroImageModal, blocksToFirstMessage
} from './intro-blocks.js';

// Example Dialogue
import {
  openExampleDialogueModal, closeExampleModal, deleteExample
} from './example-dialogue.js';

// Autofill
import {
  autoFillScenario, closeAutofillModal,
  toggleAutofillImageOption,
  selectAutofillModel, confirmAutofill, updateAutofillCost,
  loadAutofillTags, switchAutofillTab,
  selectFullGenGenre, selectFullGenModel,
  handleFullGenModeChange, toggleFullGenImageOption, updateFullGenCost, confirmFullGenerate,
  openImageGenModal, closeImageGenModal,
  selectImageGenModel, confirmImageGen
} from './autofill.js';

// Image gallery
import {
  openImageModal, closeImageModal, switchImageTab,
  handleImageUpload, generateImages,
  selectImageModel, selectImageCount, updateImageCost,
  openImageDetail, closeImageDetail, applyImage,
  downloadImage, deleteGeneratedImage,
  loadGeneratedImagesFromStorage,
  // 이미지 변형
  openSourceImageSheet, closeSourceImageSheet, uploadSourceImage,
  enterLibrarySelectMode, updateTransformCost, transformImage,
  openTransformResultDetail,
  // 에디터 바텀시트
  openEditorImageSheet, closeEditorImageSheet, uploadFromDevice,
  openLibrarySheet, closeLibrarySheet,
  openLibraryImageDetail, closeLibraryDetail, downloadLibraryImage, applyLibraryImage,
  // 이미지 생성 모드
  handleGenModeChange, isAnalysisMode
} from './image-gallery.js';

// Save/Publish
import {
  saveDraft, publishCreation, saveCreationData,
  updateStatus, confirmDelete, deleteCreation,
  duplicateCreation, showItemMenu, closeItemMenu
} from './save.js';

// Wizard (빠른 시작)
import {
  WizardModule,
  openCreationMethodModal,
  closeCreationMethodModal,
  openWizardModal,
  closeWizardModal,
  selectCreationMethod,
  bindWizardEvents
} from './wizard/index.js';

// Stats
import {
  openStats, closeStats, changePeriod,
  togglePeriodMenu, showMetricInfo, closeMetricInfo
} from './stats.js';

// ============================================
// 초기화
// ============================================

function init() {
  // 필터 버튼 이벤트
  document.querySelectorAll('.creator-filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      loadCreations(btn.dataset.filter);
    });
  });

  // 모달 닫기 버튼
  const statsModal = document.getElementById('creatorStatsModal');
  if (statsModal) {
    statsModal.addEventListener('click', (e) => {
      if (e.target === statsModal) {
        closeStats();
      }
    });
  }

  // 제작물 목록 컨테이너 이벤트 위임
  const listContainer = document.getElementById('creationsListContainer');
  if (listContainer) {
    listContainer.addEventListener('click', (e) => {
      const actionEl = e.target.closest('[data-action]');
      if (!actionEl) return;

      const action = actionEl.dataset.action;
      const id = actionEl.dataset.id || actionEl.closest('[data-id]')?.dataset.id;

      // 액션 버튼 클릭 시 상위 요소로 이벤트 전파 방지
      if (['openStats', 'openEditorById', 'showItemMenu'].includes(action)) {
        e.stopPropagation();
      }

      switch (action) {
        case 'openEditor':
          openEditor();
          break;
        case 'openDetail':
          if (id) openCreationDetail(id);
          break;
        case 'openStats':
          if (id) openStats(id);
          break;
        case 'openEditorById':
          if (id) openEditor(id);
          break;
        case 'showItemMenu':
          if (id) showItemMenu(e, id);
          break;
      }
    });
  }
}

// ============================================
// Public API - CreatorModule 객체
// ============================================

const CreatorModule = {
  // 초기화
  init,

  // 목록 관리
  loadCreations,
  renderCreationsList,
  openCreationDetail,

  // 태그 캐시 (StoryCreatorModule에서 사용)
  getAutofillTags: () => getAutofillTagsCache(),
  loadAutofillTags,

  // 타입 선택
  openCreationTypeModal,
  closeCreationTypeModal,
  selectCreationType,

  // 에디터
  openEditor,
  closeEditor,
  switchEditorTab,
  showEditorMenu,
  resetEditor,
  deleteFromEditor,

  // 인트로 블록 관리
  setIntroType,
  addIntroBlock,
  editIntroBlock,
  moveIntroBlock,
  deleteIntroBlock,
  selectIntroBlock,
  addIntroImage,
  closeIntroImageModal,
  insertIntroImage,
  editIntroImage,
  replaceIntroImage,

  // 대화 예시
  openExampleDialogueModal,
  closeExampleModal,
  deleteExample,

  // 자동 생성
  autoFillScenario,
  closeAutofillModal,
  toggleAutofillImageOption,
  selectAutofillModel,
  confirmAutofill,
  updateAutofillCost,

  // 전체 자동 생성 탭
  switchAutofillTab,
  selectFullGenGenre,
  selectFullGenModel,
  handleFullGenModeChange,
  toggleFullGenImageOption,
  updateFullGenCost,
  confirmFullGenerate,

  // 이미지 자동 생성
  openImageGenModal,
  closeImageGenModal,
  selectImageGenModel,
  confirmImageGen,
  updateImageGenButtonState,

  // 이미지 모달
  openImageModal,
  closeImageModal,
  switchImageTab,
  handleImageUpload,
  generateImages,
  selectImageModel,
  selectImageCount,
  updateImageCost,
  openImageDetail,
  closeImageDetail,
  applyImage,
  downloadImage,
  deleteGeneratedImage,

  // 이미지 변형
  openSourceImageSheet,
  closeSourceImageSheet,
  uploadSourceImage,
  enterLibrarySelectMode,
  updateTransformCost,
  transformImage,
  openTransformResultDetail,

  // 이미지 생성 모드
  handleGenModeChange,
  isAnalysisMode,

  // 에디터 바텀시트
  openEditorImageSheet,
  closeEditorImageSheet,
  uploadFromDevice,
  openLibrarySheet,
  closeLibrarySheet,
  openLibraryImageDetail,
  closeLibraryDetail,
  downloadLibraryImage,
  applyLibraryImage,

  // 다중 이미지 관리
  removeEditorImage,
  selectMainImage,
  openEditorImagePreview,
  closeEditorImagePreview,
  setMainFromPreview,
  removeFromPreview,
  downloadPreviewImage,

  // 저장/등록
  saveDraft,
  publishCreation,

  // 통계
  openStats,
  closeStats,
  changePeriod,
  togglePeriodMenu,
  showMetricInfo,
  closeMetricInfo,

  // 아이템 관리
  showItemMenu,
  updateStatus,
  confirmDelete,
  duplicateCreation,

  // 검색
  openSearch,
  closeSearch,
  handleSearch,
  clearSearch,

  // 정렬
  openSortModal,
  closeSortModal,
  setSort,

  // 캐시 관리
  invalidateCache
};

// ============================================
// 하위 호환성: window 객체에 노출
// (추후 제거 예정 - ES6_MIGRATION_PLAN.md 참조)
// ============================================

window.CreatorModule = CreatorModule;

// ============================================
// DOM 로드 시 초기화
// ============================================

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// ============================================
// ES6 모듈 export
// ============================================

export default CreatorModule;

export {
  // 목록 관리
  loadCreations,
  renderCreationsList,
  openCreationDetail,

  // 검색/정렬
  openSearch,
  closeSearch,
  handleSearch,
  clearSearch,
  openSortModal,
  closeSortModal,
  setSort,

  // 에디터
  openEditor,
  closeEditor,
  switchEditorTab,
  showEditorMenu,
  resetEditor,
  deleteFromEditor,
  openCreationTypeModal,
  closeCreationTypeModal,
  selectCreationType,

  // 인트로 블록
  renderIntroPreview,
  setIntroType,
  addIntroBlock,
  editIntroBlock,
  moveIntroBlock,
  deleteIntroBlock,
  selectIntroBlock,
  addIntroImage,
  insertIntroImage,
  editIntroImage,
  replaceIntroImage,
  closeIntroImageModal,

  // 대화 예시
  openExampleDialogueModal,
  closeExampleModal,
  deleteExample,

  // 자동 생성
  autoFillScenario,
  closeAutofillModal,
  toggleAutofillImageOption,
  selectAutofillModel,
  confirmAutofill,
  updateAutofillCost,
  loadAutofillTags,
  switchAutofillTab,
  selectFullGenGenre,
  selectFullGenModel,
  handleFullGenModeChange,
  toggleFullGenImageOption,
  updateFullGenCost,
  confirmFullGenerate,
  openImageGenModal,
  closeImageGenModal,
  selectImageGenModel,
  confirmImageGen,
  updateImageGenButtonState,

  // 이미지 갤러리
  openImageModal,
  closeImageModal,
  switchImageTab,
  handleImageUpload,
  generateImages,
  selectImageModel,
  selectImageCount,
  updateImageCost,
  openImageDetail,
  closeImageDetail,
  applyImage,
  downloadImage,
  deleteGeneratedImage,
  loadGeneratedImagesFromStorage,

  // 이미지 변형
  openSourceImageSheet,
  closeSourceImageSheet,
  uploadSourceImage,
  enterLibrarySelectMode,
  updateTransformCost,
  transformImage,
  openTransformResultDetail,

  // 이미지 생성 모드
  handleGenModeChange,
  isAnalysisMode,

  // 에디터 바텀시트
  openEditorImageSheet,
  closeEditorImageSheet,
  uploadFromDevice,
  openLibrarySheet,
  closeLibrarySheet,
  openLibraryImageDetail,
  closeLibraryDetail,
  downloadLibraryImage,
  applyLibraryImage,

  // 에디터 이미지
  renderEditorImages,
  addEditorImage,
  removeEditorImage,
  selectMainImage,
  openEditorImagePreview,
  closeEditorImagePreview,
  setMainFromPreview,
  removeFromPreview,
  downloadPreviewImage,

  // 저장/등록
  saveDraft,
  publishCreation,
  saveCreationData,
  updateStatus,
  confirmDelete,
  deleteCreation,
  duplicateCreation,
  showItemMenu,
  closeItemMenu,

  // 통계
  openStats,
  closeStats,
  changePeriod,
  togglePeriodMenu,
  showMetricInfo,
  closeMetricInfo,

  // 캐시
  invalidateCache,

  // 마법사 (빠른 시작)
  WizardModule,
  openCreationMethodModal,
  closeCreationMethodModal,
  openWizardModal,
  closeWizardModal,
  selectCreationMethod,
  bindWizardEvents
};
